import React, { useState, useEffect } from 'react';
import authService from '../services/authService';
import Button from './ui/Button';
import './Login.css';
import './AuthPages.css';

/**
 * #81 — Página "Aceitar convite" — rota /accept-invite?token=XXX.
 *
 * O app não usa React Router: App.jsx detecta o pathname e renderiza esta
 * página. O token vem na query string do link enviado por e-mail.
 *
 * Fluxo: prévia (GET /auth/accept-invite?token=) mostra empresa/email/papel →
 * o convidado define nome + senha → POST /auth/accept-invite cria o usuário
 * no tenant e já faz auto-login → redireciona pro app.
 */
function getTokenFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get('token') || '';
  } catch (e) {
    return '';
  }
}

const ROLE_LABEL = { admin: 'Administrador', operador: 'Operador', auditor: 'Auditor' };

function AcceptInvite() {
  const [token] = useState(getTokenFromUrl);
  const [preview, setPreview] = useState(null);       // {email, empresa, role}
  const [previewErro, setPreviewErro] = useState('');
  const [carregando, setCarregando] = useState(true);

  const [nome, setNome] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let vivo = true;
    if (!token) { setCarregando(false); return; }
    (async () => {
      try {
        const p = await authService.previewInvite(token);
        if (vivo) setPreview(p);
      } catch (err) {
        if (vivo) setPreviewErro(err.message || 'Convite inválido ou expirado.');
      } finally {
        if (vivo) setCarregando(false);
      }
    })();
    return () => { vivo = false; };
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!nome.trim()) { setError('Informe seu nome.'); return; }
    if (senha.length < 8) { setError('A senha deve ter pelo menos 8 caracteres.'); return; }
    if (senha !== confirmacao) { setError('As senhas não coincidem.'); return; }

    setLoading(true);
    try {
      await authService.acceptInvite(token, nome.trim(), senha);
      // Auto-login feito no service — entra direto no app.
      window.location.href = '/';
    } catch (err) {
      setError(err.message || 'Não foi possível aceitar o convite.');
    } finally {
      setLoading(false);
    }
  };

  // Token ausente ou convite inválido/expirado → estado terminal.
  if (!token || (!carregando && (previewErro || !preview))) {
    return (
      <div className="login-page-wrapper">
        <div className="login-form-column auth-card">
          <div className="login-form-header">
            <h2 className="form-title">Convite inválido</h2>
            <p className="form-subtitle">
              {previewErro || 'Este link de convite é inválido, expirado ou já foi usado. Peça um novo convite ao administrador da conta.'}
            </p>
          </div>
          <div className="auth-back">
            <a href="/" className="forgot-link">← Ir para o login</a>
          </div>
        </div>
      </div>
    );
  }

  if (carregando) {
    return (
      <div className="login-page-wrapper">
        <div className="login-form-column auth-card">
          <div className="login-form-header">
            <h2 className="form-title">Carregando convite…</h2>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-page-wrapper">
      <div className="login-form-column auth-card">
        <div className="login-form-header">
          <h2 className="form-title">Aceitar convite</h2>
          <p className="form-subtitle">
            Você foi convidado para a equipe de <strong>{preview.empresa || 'sua empresa'}</strong>
            {preview.role ? <> como <strong>{ROLE_LABEL[preview.role] || preview.role}</strong></> : null}.
            Crie sua conta para <strong>{preview.email}</strong>.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="login-form-main">
          <div className="form-group">
            <label className="form-label">Seu nome</label>
            <div className="input-wrapper">
              <span className="material-symbols-outlined input-icon">person</span>
              <input
                type="text"
                className="form-input"
                placeholder="Nome completo"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Crie uma senha</label>
            <div className="input-wrapper">
              <span className="material-symbols-outlined input-icon">lock</span>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="••••••••"
                value={senha}
                onChange={(e) => setSenha(e.target.value)}
                required
                disabled={loading}
              />
              <button
                type="button"
                className="visibility-btn"
                onClick={() => setShowPassword(!showPassword)}
              >
                <span className="material-symbols-outlined">
                  {showPassword ? 'visibility_off' : 'visibility'}
                </span>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Confirmar senha</label>
            <div className="input-wrapper">
              <span className="material-symbols-outlined input-icon">lock</span>
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                placeholder="••••••••"
                value={confirmacao}
                onChange={(e) => setConfirmacao(e.target.value)}
                required
                disabled={loading}
              />
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <Button type="submit" className="submit-btn" disabled={loading}>
            {loading ? 'CRIANDO CONTA...' : 'ACEITAR E ENTRAR'}
            <span className="material-symbols-outlined" style={{ marginLeft: '0.5rem' }}>arrow_forward</span>
          </Button>

          <div className="auth-back">
            <a href="/" className="forgot-link">← Já tenho conta</a>
          </div>
        </form>
      </div>
    </div>
  );
}

export default AcceptInvite;
